import { PasswordRequirements } from '@/components/auth/PasswordRequirements';
import { Card } from '@/components/ui';
import { Alert } from '@/components/ui/alert';
import { Field } from '@/components/ui/field';
import { LoadingPage } from '@/components/ui/loading-page';
import { useAuth } from '@/contexts/auth-context';
import { useResetPasswordAction } from '@/hooks/actions/use-reset-password-action';
import { useVerifyPasswordResetTokenAction } from '@/hooks/actions/use-verify-password-reset-token-action';
import {
  Box,
  Button,
  Container,
  Fieldset,
  Heading,
  Input,
  Link,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Logo } from '@fluenceur/ui';
import { zodResolver } from '@hookform/resolvers/zod';

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { CheckCircle } from 'iconoir-react';

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const { refreshCurrentUser } = useAuth();
  const verifyTokenMutation = useVerifyPasswordResetTokenAction();
  const resetPasswordMutation = useResetPasswordAction();

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setErrorMessage(t('auth.resetPassword.invalidToken'));
        setIsVerifying(false);
        return;
      }

      try {
        await verifyTokenMutation.mutateAsync(token);
        setIsValidToken(true);
      } catch (error: any) {
        const message = error?.response?.data?.message || t('auth.resetPassword.invalidToken');
        setErrorMessage(message);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token, t]);

  // Schema definition with translated messages
  const resetPasswordSchema = z
    .object({
      password: z
        .string()
        .min(1, t('auth.validation.passwordRequired'))
        .min(8, t('auth.validation.passwordMinLength'))
        .regex(/[A-Za-z]/, t('auth.validation.passwordLetters'))
        .regex(/[0-9]/, t('auth.validation.passwordNumbers')),
      passwordConfirmation: z.string().min(1, t('auth.validation.passwordConfirmationRequired')),
    })
    .refine((data) => data.password === data.passwordConfirmation, {
      message: t('auth.validation.passwordsDoNotMatch'),
      path: ['passwordConfirmation'],
    });

  type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      passwordConfirmation: '',
    },
  });

  const password = watch('password');
  const passwordConfirmation = watch('passwordConfirmation');

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) return;

    setErrorMessage(null);
    try {
      // Reset password and automatically receive authentication tokens
      await resetPasswordMutation.mutateAsync({
        token,
        password: data.password,
        passwordConfirmation: data.passwordConfirmation,
      });

      // Fetch user data after successful password reset (tokens already stored)
      await refreshCurrentUser();

      setIsSuccess(true);

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error: any) {
      const message = error?.response?.data?.message || t('auth.errors.unexpectedError');
      setErrorMessage(message);
    }
  };

  // Show loading while verifying token
  if (isVerifying) {
    return <LoadingPage />;
  }

  // Show error if token is invalid
  if (!isValidToken) {
    return (
      <Box
        minHeight="100vh"
        bg="whiteSmoke"
        width="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        py={{ base: 12, md: 16 }}
      >
        <VStack gap={6} align="center" width="full">
          <Logo h="28px" />
          <Container maxW="500px" width="full">
            <Box bg="white" rounded="xl" p={{ base: 6, sm: 8 }}>
              <VStack gap={4}>
                <Heading as="h1" size="xl" fontWeight="medium" textAlign="center">
                  {t('auth.resetPassword.invalidLinkTitle')}
                </Heading>
                <Alert status="error" title={t('common.error')}>
                  {errorMessage}
                </Alert>
                <Text fontSize="md" color="gray.600" textAlign="center">
                  {t('auth.resetPassword.requestNewLink')}
                </Text>
                <Button colorPalette="primary" size="lg" asChild mt={4}>
                  <Link href="/forgot-password" _hover={{ textDecoration: 'none' }}>
                    {t('auth.resetPassword.requestNewReset')}
                  </Link>
                </Button>
              </VStack>
            </Box>
          </Container>
        </VStack>
      </Box>
    );
  }

  // Show success message after password reset
  if (isSuccess) {
    return (
      <Box
        minHeight="100vh"
        bg="whiteSmoke"
        width="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        py={{ base: 12, md: 16 }}
      >
        <VStack gap={6} align="center" width="full">
          <Logo h="28px" />
          <Container maxW="500px" width="full">
            <Box bg="white" rounded="xl" p={{ base: 6, sm: 8 }}>
              <VStack gap={4}>
                <Box color="green.500" fontSize="4xl">
                  <CheckCircle />
                </Box>
                <Heading as="h1" size="xl" fontWeight="medium" textAlign="center">
                  {t('auth.resetPassword.successTitle')}
                </Heading>
                <Alert status="success" title={t('common.success')} textAlign="center">
                  {t('auth.resetPassword.successMessage')}
                </Alert>
                <Text fontSize="md" color="gray.600" textAlign="center">
                  {t('auth.resetPassword.redirecting')}
                </Text>
                <Button colorPalette="primary" size="lg" asChild mt={4}>
                  <Link href="/" _hover={{ textDecoration: 'none' }}>
                    {t('auth.resetPassword.goToLogin')}
                  </Link>
                </Button>
              </VStack>
            </Box>
          </Container>
        </VStack>
      </Box>
    );
  }

  // Show reset password form
  return (
    <Box
      minHeight="100vh"
      bg="whiteSmoke"
      width="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      py={{ base: 12, md: 16 }}
    >
      <VStack gap={6} align="center" width="full">
        <Logo h="28px" />
        <Container maxW="500px" width="full">
          {/* Reset Password Form Card */}
          <Card.Root bg="babyPowder">
            <Card.Body>
              <Stack as="form" onSubmit={handleSubmit(onSubmit)} gap={4} width="full">
                {/* Title and Description */}
                <VStack gap={{ base: 2, md: 3 }} mb={2}>
                  <Heading as="h1" size="xl" fontWeight="medium" textAlign="center">
                    {t('auth.resetPassword.title')}
                  </Heading>
                  <Text fontSize="md" color="gray.600" textAlign="center">
                    {t('auth.resetPassword.description')}
                  </Text>
                </VStack>

                {/* Error Alert */}
                {errorMessage && (
                  <Alert status="error" title={t('common.error')} textAlign="center">
                    {errorMessage}
                  </Alert>
                )}

                {/* Form Fields */}
                <Fieldset.Root size="lg" width="100%" colorPalette="gray">
                  <Fieldset.Content>
                    <VStack gap={4} width="full">
                      <Field
                        label={t('auth.newPassword')}
                        errorText={errors.password?.message}
                        required
                      >
                        <Input
                          {...register('password')}
                          type="password"
                          placeholder={t('auth.newPasswordPlaceholder')}
                          variant="outline"
                          size="lg"
                          autoComplete="new-password"
                        />
                      </Field>

                      <Field
                        label={t('auth.confirmPassword')}
                        errorText={errors.passwordConfirmation?.message}
                        required
                      >
                        <Input
                          {...register('passwordConfirmation')}
                          type="password"
                          placeholder={t('auth.confirmPasswordPlaceholder')}
                          variant="outline"
                          size="lg"
                          autoComplete="new-password"
                        />
                      </Field>

                      {/* Password Requirements Indicator */}
                      <PasswordRequirements
                        password={password}
                        passwordConfirmation={passwordConfirmation}
                        showMatchRequirement
                      />
                    </VStack>
                  </Fieldset.Content>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    colorPalette="primary"
                    size="lg"
                    width="full"
                    loading={isSubmitting}
                    loadingText={t('auth.resetPassword.resetting')}
                    h={12}
                    mt={{ base: 4, md: 6 }}
                  >
                    {t('auth.resetPassword.resetPassword')}
                  </Button>
                </Fieldset.Root>
              </Stack>
            </Card.Body>
          </Card.Root>
        </Container>
        <Link href="mailto:contact@fluenceur.com" fontSize="sm">
          {t('auth.needHelp')}
        </Link>
      </VStack>
    </Box>
  );
};

export default ResetPasswordPage;
